import { MetricData } from '../Constants/metricTypes';
import { MetricConfig } from '../Constants/metricsList';
import { 
  STATUS_VALUES, 
  STATUS_TRANSFORMATIONS, 
  LineChartSeries, 
  AreaChartSeries,
  BaseChartDataPoint,
  StatusValue
} from '../Constants/chartTypes';

// Helper type for the return type
type ChartSeriesType = LineChartSeries | AreaChartSeries;

export const generateMetricsJson = (metricConfig: MetricConfig): string => {
  const metrics = metricConfig.paramSets.flatMap(paramSet => 
    paramSet.metricNames.map(metricName => ({
      metric_name: metricName,
      stats: paramSet.stats
    }))
  );
  return JSON.stringify(metrics);
};

export const combineClusterStatusMetrics = (
  timestamps: number[], 
  metricData: MetricData['metricData'], 
  metricConfig: MetricConfig
): ChartSeriesType => {
  const redMetric = metricData.find(m => m.metricName === 'ClusterStatus.red');
  const yellowMetric = metricData.find(m => m.metricName === 'ClusterStatus.yellow');
  const greenMetric = metricData.find(m => m.metricName === 'ClusterStatus.green');

  const combinedData: BaseChartDataPoint[] = timestamps.map((timestamp: number, index: number) => {
    let statusValue: StatusValue = STATUS_VALUES.GREEN;
    
    if (redMetric?.stats[0]?.values[index] === 1) {
      statusValue = STATUS_VALUES.RED;
    } else if (yellowMetric?.stats[0]?.values[index] === 1) {
      statusValue = STATUS_VALUES.YELLOW;
    } else if (greenMetric?.stats[0]?.values[index] === 1) {
      statusValue = STATUS_VALUES.GREEN;
    }

    return {
      x: new Date(timestamp * 1000),
      y: statusValue
    };
  });

  return {
    title: 'Cluster Status',
    type: metricConfig.chartType,
    data: combinedData
  } as ChartSeriesType;
};

export const transformBooleanStatusMetrics = (
  timestamps: number[], 
  metricData: MetricData['metricData'], 
  metricConfig: MetricConfig
): ChartSeriesType => {
  const metric = metricData[0];
  
  const transformValue = STATUS_TRANSFORMATIONS[metricConfig.title] || 
    ((value: number) => value);

  const transformedData: BaseChartDataPoint[] = timestamps.map((timestamp: number, index: number) => {
    const originalValue = metric?.stats[0]?.values[index] ?? 0;
    const transformedValue = transformValue(originalValue);

    return {
      x: new Date(timestamp * 1000),
      y: transformedValue
    };
  });

  return {
    title: metricConfig.title,
    type: metricConfig.chartType,
    data: transformedData
  } as ChartSeriesType;
};

export const transformDataToChartSeries = (
  apiData: MetricData | null, 
  metricConfig: MetricConfig
): ChartSeriesType[] => {
  if (!apiData?.timestamps || !apiData?.metricData) {
    return [];
  }

  const { timestamps, metricData } = apiData;
  const series: ChartSeriesType[] = [];

  if (metricConfig.statusMapping) {
    if (metricConfig.title === 'Cluster status') {
      series.push(combineClusterStatusMetrics(timestamps, metricData, metricConfig));
    } else {
      series.push(transformBooleanStatusMetrics(timestamps, metricData, metricConfig));
    }
  } else {
    metricData.forEach((metric) => {
      metric.stats.forEach((statData) => {
        const chartData: BaseChartDataPoint[] = timestamps.map((timestamp: number, index: number) => ({
          x: new Date(timestamp * 1000),
          y: statData.values[index] ?? 0
        }));

        const seriesTitle = metricConfig.titleSource === 'stat' 
          ? statData.stat.toUpperCase()
          : metric.metricName;
        
        series.push({
          title: seriesTitle,
          type: metricConfig.chartType,
          data: chartData
        } as ChartSeriesType);
      });
    });
  }

  return series;
};

// Type guard to check if series is LineChartSeries
export const isLineChartSeries = (series: ChartSeriesType): series is LineChartSeries => {
  return series.type === 'line';
};

// Type guard to check if series is AreaChartSeries
export const isAreaChartSeries = (series: ChartSeriesType): series is AreaChartSeries => {
  return series.type === 'area';
};

export function lastNonZero(
  data: Array<number | null>,
  epsilon = 0
): number {
  for (let i = data.length - 1; i >= 0; i--) {
    const v = data[i];
    if (typeof v === "number" && Number.isFinite(v) && Math.abs(v) > epsilon) {
      return v;
    }
  }
  return 0;
}


export const calculateValueAsPerStat = (values: any, stat: string) => {
  const sanitizedValues = Array.isArray(values) ? values.map(i => isNaN(i) ? 0 : i) : [0];
  const n = Array.isArray(sanitizedValues) ? sanitizedValues.length : 0;
  if (n === 0) {
    return 0;
  }
  if (stat === 'min') {
    return Math.min(...sanitizedValues);
  }
  if (stat === 'max') {
    return Math.max(...sanitizedValues);
  }
  if (stat === 'avg' || stat === 'sum' || stat === 'count') {
    const sum = sanitizedValues.reduce((a, b) => a + b, 0);
    return sum / n;
  }
  return lastNonZero(sanitizedValues);
};

export const transformMetricSummary = (
  raw: any,
  METRICS: string[],
  nodeId: string,
  filters: Record<string, string>,
  isShardMetrics: boolean = false
): Record<string, number | string> => {
  const result: Record<string, number | string> = { nodeId };

  for (const metric of METRICS) {
    result[metric] = '-';
  }

  if (raw?.status === 'fulfilled' && raw.value?.metricData && Array.isArray(raw.value.metricData)) {
    for (const metricEntry of raw.value.metricData) {
      const name = metricEntry.metricName;
      const stats = metricEntry.stats ?? [];
      const avgStat = stats.find((s: any) => filters.statistics === s.stat);
      let values = avgStat?.values ?? [];
      if (isShardMetrics && metricEntry.metricName === 'shard_cpu_utilization') {
        values = values.map(v => v * 100);
      }
      if (values.length > 0 && METRICS.includes(name)) {
        const val = calculateValueAsPerStat(values, filters.statistics);
        if (!isNaN(val)) result[name] = val?.toFixed(2);
      }
    }
  }
  return result;
};